import { FormControl, FormGroup, Validators } from '@angular/forms';
import { GoalKey, Namespace, SituationConfig, SituationMapping } from '@vendasta/protos/ai_assistants/v1alpha1';
import { FormArrayWithDirtyTracking } from './forms';

export interface ForkedCapabilityMapping {
  oldKey: Partial<GoalKey>;
  newKey: Partial<GoalKey>;
}

function areNamespacesEqual(
  ns1: Namespace | null | undefined,
  ns2: Namespace | null | undefined,
): boolean {
  if (!ns1 && !ns2) return true;
  if (!ns1 || !ns2) return false;
  return (
    ns1.accountGroupNamespace?.accountGroupId === ns2.accountGroupNamespace?.accountGroupId &&
    ns1.partnerNamespace?.partnerId === ns2.partnerNamespace?.partnerId &&
    !!ns1.systemNamespace === !!ns2.systemNamespace &&
    !!ns1.globalNamespace === !!ns2.globalNamespace
  );
}

export function areGoalKeysEqual(
  key1: Partial<GoalKey> | null | undefined,
  key2: Partial<GoalKey> | null | undefined,
): boolean {
  if (!key1 || !key2) return false;
  if (key1.id !== key2.id) return false;
  return areNamespacesEqual(key1.namespace, key2.namespace);
}

export type AiSituationMappingFormControls = {
  situation: FormControl<string | null>;
  goalKeys: FormControl<Partial<GoalKey>[] | null>;
};

export class AiSituationMappingForm extends FormGroup<AiSituationMappingFormControls> {
  constructor(situationMapping?: { situation?: string; goalKeys?: Partial<GoalKey>[] }) {
    super({
      situation: new FormControl(situationMapping?.situation ?? null, Validators.required),
      goalKeys: new FormControl(situationMapping?.goalKeys ?? [], Validators.required),
    });
  }

  toSituationMapping(): Partial<SituationMapping> {
    return {
      situation: this.controls.situation.value ?? undefined,
      goalKeys: (this.controls.goalKeys.value ?? undefined) as SituationMapping['goalKeys'] | undefined,
    };
  }
}

export class AiSituationMappingFormArray extends FormArrayWithDirtyTracking<AiSituationMappingForm> {
  constructor(situationMappings?: Array<{ situation?: string; goalKeys?: Partial<GoalKey>[] }>) {
    super((situationMappings ?? []).map((mapping) => new AiSituationMappingForm(mapping)));
  }

  toSituationMappings(): Partial<SituationMapping>[] {
    return this.controls.map((mappingForm) => mappingForm.toSituationMapping());
  }
}

export type AiSituationConfigFormControls = {
  situationMappings: AiSituationMappingFormArray;
};

export class AiSituationConfigForm extends FormGroup<AiSituationConfigFormControls> {
  constructor(situationConfig?: { situationMappings?: Array<{ situation?: string; goalKeys?: Partial<GoalKey>[] }> }) {
    super({
      situationMappings: new AiSituationMappingFormArray(situationConfig?.situationMappings),
    });
  }

  toSituationConfig(): Partial<SituationConfig> {
    return {
      simpleConfig: this.controls.situationMappings.toSituationMappings() as SituationConfig['simpleConfig'],
    };
  }

  updateForkedCapabilityReferences(forkedMappings: ForkedCapabilityMapping[]): void {
    for (const mapping of this.controls.situationMappings.controls) {
      const currentGoalKeys = mapping.controls.goalKeys.value ?? [];
      let updated = false;
      const updatedGoalKeys = currentGoalKeys.map((key) => {
        const forkedMapping = forkedMappings.find((m) => areGoalKeysEqual(m.oldKey, key));
        if (forkedMapping) {
          updated = true;
          return forkedMapping.newKey;
        }
        return key;
      });
      if (updated) {
        mapping.controls.goalKeys.setValue(updatedGoalKeys, { emitEvent: false });
        mapping.controls.goalKeys.markAsDirty({ onlySelf: true });
      }
    }
  }

  getSituationsReferencingCapability(capabilityKey: Partial<GoalKey> | null): string[] {
    return this.controls.situationMappings.controls
      .filter((mapping) => {
        const goalKeys = mapping.controls.goalKeys.value ?? [];
        return goalKeys.some((key) => areGoalKeysEqual(key, capabilityKey));
      })
      .map((mapping) => mapping.controls.situation.value ?? '');
  }

  removeCapabilityFromMappings(capabilityKey: Partial<GoalKey> | null): void {
    const mappingsToRemove: number[] = [];

    this.controls.situationMappings.controls.forEach((mapping, index) => {
      const currentGoalKeys = mapping.controls.goalKeys.value ?? [];
      const filteredGoalKeys = currentGoalKeys.filter((key) => !areGoalKeysEqual(key, capabilityKey));

      if (filteredGoalKeys.length === 0) {
        // remove situation if it no longer has any capabilities
        mappingsToRemove.push(index);
      } else if (filteredGoalKeys.length !== currentGoalKeys.length) {
        mapping.controls.goalKeys.setValue(filteredGoalKeys);
        mapping.controls.goalKeys.markAsDirty();
      }
    });

    for (const index of mappingsToRemove.reverse()) {
      this.controls.situationMappings.removeAt(index);
      this.controls.situationMappings.markAsDirty();
    }
  }
}
